import React from "react";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import { Skeleton } from "../ui/skeleton";
import { Checkbox } from "../ui/checkbox";
import { cn } from "../../lib/utils";
import { EmptyState } from "./EmptyState";
import { ErrorState } from "./ErrorState";

export function DataTable({
  columns, rows, loading, error, onRetry, onRowClick,
  emptyTitle, emptyDescription, testId,
  selectable, selectedIds, onSelectionChange,
  rowClassName, groupKey, renderGroupHeader,
}) {
  if (error) return <ErrorState message={error} onRetry={onRetry} />;

  const selected = selectedIds || [];
  const allSelected = rows.length > 0 && rows.every((r) => selected.includes(r.id));

  const toggleAll = () => {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? [] : rows.map((r) => r.id));
  };

  const toggleRow = (id) => {
    if (!onSelectionChange) return;
    onSelectionChange(
      selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]
    );
  };

  let prevGroup;
  let prevGroupMobile;

  const leadingCol = columns.find((c) => c.leading) || columns.find((c) => c.key === "id");
  const primaryCol = columns.find((c) => c.primary) || columns.find((c) => c !== leadingCol) || columns[0];
  const subtitleCol = columns.find((c) => c.subtitle);
  const badgeCol = columns.find((c) => c.badge || c.key === "status");
  const footerCol = columns.find((c) => c.footer || c.key === "actions");
  const metaCols = columns.filter(
    (c) => c !== leadingCol && c !== primaryCol && c !== subtitleCol && c !== badgeCol && c !== footerCol
  );

  const renderCard = (row, i) => (
    <div
      key={row.id ?? i}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
      className={`rounded-lg border border-border bg-surface p-3.5 ${onRowClick ? "cursor-pointer active:bg-background" : ""} ${rowClassName ? rowClassName(row) : ""}`}
      data-testid={`table-card-${row.id ?? i}`}
    >
      <div className="flex items-start gap-3">
        {selectable && (
          <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
            <Checkbox
              checked={selected.includes(row.id)}
              onCheckedChange={() => toggleRow(row.id)}
              data-testid={`table-row-checkbox-${row.id ?? i}`}
            />
          </div>
        )}
        <div className="min-w-0 flex-1">
          {leadingCol && (
            <p className="text-[11px] text-text-muted">#{leadingCol.render ? leadingCol.render(row) : row[leadingCol.key]}</p>
          )}
          <div className="truncate text-sm font-semibold text-text-main">
            {primaryCol.render ? primaryCol.render(row) : row[primaryCol.key]}
          </div>
          {subtitleCol && (
            <div className="truncate text-xs text-text-muted">
              {subtitleCol.render ? subtitleCol.render(row) : row[subtitleCol.key]}
            </div>
          )}
        </div>
        {badgeCol && <div className="flex-shrink-0">{badgeCol.render ? badgeCol.render(row) : row[badgeCol.key]}</div>}
      </div>
      {metaCols.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
          {metaCols.map((col) => (
            <div key={col.key} className="min-w-0">
              <span className="text-text-muted">{col.header}: </span>
              <span className="break-words text-text-main">{col.render ? col.render(row) : row[col.key] ?? "—"}</span>
            </div>
          ))}
        </div>
      )}
      {footerCol && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border pt-2.5" onClick={(e) => e.stopPropagation()}>
          {footerCol.render ? footerCol.render(row) : row[footerCol.key]}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid={testId}>
      <div className="hidden overflow-x-auto rounded-md border border-border bg-surface md:block">
        <Table>
          <TableHeader>
            <TableRow>
              {selectable && (
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    disabled={rows.length === 0}
                    data-testid="table-select-all-checkbox"
                  />
                </TableHead>
              )}
              {columns.map((col, ci) => (
                <TableHead key={col.key} className={cn((ci > 0 || selectable) && "border-l border-border", col.headerClassName)}>{col.header}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  {selectable && <TableCell><Skeleton className="h-4 w-4" /></TableCell>}
                  {columns.map((col) => (
                    <TableCell key={col.key}>
                      <Skeleton className="h-4 w-full max-w-[140px]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (selectable ? 1 : 0)} className="p-0">
                  <EmptyState title={emptyTitle || "No records found"} description={emptyDescription} />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, i) => {
                const group = groupKey ? groupKey(row) : undefined;
                const showGroupHeader = groupKey && group !== prevGroup;
                prevGroup = group;
                return (
                  <React.Fragment key={row.id ?? i}>
                    {showGroupHeader && (
                      <TableRow className="pointer-events-none hover:bg-transparent" data-testid={`table-group-header-${group}`}>
                        <TableCell
                          colSpan={columns.length + (selectable ? 1 : 0)}
                          className="border-t-2 border-border bg-background px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted"
                        >
                          {renderGroupHeader(group)}
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      className={cn(
                        i % 2 === 1 ? "bg-background/60" : "bg-surface",
                        onRowClick && "cursor-pointer hover:bg-accent/5",
                        rowClassName && rowClassName(row)
                      )}
                      data-testid={`table-row-${row.id ?? i}`}
                    >
                      {selectable && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selected.includes(row.id)}
                            onCheckedChange={() => toggleRow(row.id)}
                            data-testid={`table-row-checkbox-${row.id ?? i}`}
                          />
                        </TableCell>
                      )}
                      {columns.map((col, ci) => (
                        <TableCell key={col.key} className={cn((ci > 0 || selectable) && "border-l border-border", col.className)}>
                          {col.render ? col.render(row) : row[col.key]}
                        </TableCell>
                      ))}
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <div className="md:hidden">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full rounded-lg" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-border bg-surface">
            <EmptyState title={emptyTitle || "No records found"} description={emptyDescription} />
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((row, i) => {
              const group = groupKey ? groupKey(row) : undefined;
              const showGroupHeader = groupKey && group !== prevGroupMobile;
              prevGroupMobile = group;
              return (
                <React.Fragment key={row.id ?? i}>
                  {showGroupHeader && (
                    <div className="px-1 pt-2 text-xs font-semibold uppercase tracking-wide text-text-muted" data-testid={`table-mobile-group-header-${group}`}>
                      {renderGroupHeader(group)}
                    </div>
                  )}
                  {renderCard(row, i)}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
